import React from 'react';
import { ShirtConfiguration, ShirtData, SizeOrder } from './LineItemTypes';
import { CurrencyAmount, CurrencyFormattingDetails, formatCurrencyValue } from '../../../utils/currency';
import QtySelector from '../../inputs/QtySelector';



interface ShirtSizeSelectorProps
{
    shirtUnitPrice: CurrencyAmount
    lineItemId: number
    itemConfiguration: ShirtConfiguration,
    onDataChange : (lineItemId: number, newData: ShirtData, isValid: boolean, errorMsg?: string) => void,
    lineItemData: ShirtData,
    disableInputs ?: boolean,
    quantity: number
    onPriceChange : (lineItemId: number, newAmount: CurrencyAmount) => void,
    onPriceDisplayChange: (newValue: string) => void,
    onQuantityChange : (qty: number) => void,
    onPriceValidityChange: (lineItemId: number, isValid: boolean, errormMsg?: string) => void
}

const ShirtSizeSelector = (props:ShirtSizeSelectorProps) =>
{
    const constructDefaultShirtMap = () =>
    {
        return props.itemConfiguration.sizeConfiguration.reduce((map, sizeConfig) =>
        {
            return({...map, [sizeConfig.dbId] : {
                dbId: sizeConfig.dbId,
                quantity: props.lineItemData.sizes.find((size) => { return size.dbId === sizeConfig.dbId })?.quantity ?? 0,
                unitPrice: sizeConfig.unitPrice,
                displayName: sizeConfig.displayName,
                maxInventory: sizeConfig.maxInventory
            }})
        },{});
    }

    const [shirtMap, setShirtMap] = React.useState<Record<number,SizeOrder>>(constructDefaultShirtMap());
    const [errorMsg, setErrorMsg] = React.useState<string | undefined>();
    const [quantity, setQuantity] = React.useState<number>(props.quantity);

    console.log(shirtMap);


    const onSizeDataChange = (dbId: number, quantity: number) =>
    {
        setShirtMap((oldMap) => {
            return{...oldMap, [dbId]: {
                ...oldMap[dbId],
                quantity: quantity
            }}
        })
    }

    const renderShirtSizes = () =>
    {
        const temp = Object.entries(shirtMap);
        return temp.map(([id, size]) => 
        {
            return(<ShirtSizeLine
                    max={size.maxInventory}
                    shirtUnitPrice={props.shirtUnitPrice}
                    shirtDbId={Number(id)}
                    shirtDisplayName={size.displayName as string}
                    shirtData={size}
                    disableInputs={props.disableInputs}
                    onSizeDataChange={onSizeDataChange}/>)
        })
    }

    const onTotalQuantityChange = (newQuant: number) =>
    {
        setQuantity(newQuant);
        props.onQuantityChange(newQuant);
    }

    React.useEffect(() => 
    {
        const totalPrice = Object.entries(shirtMap).reduce((acc, [id, size]) => 
        {
            console.log(size.unitPrice);
            return acc + (size.quantity * (size.unitPrice.value.float ?? 0));
        },0);

        const totalQty = Object.entries(shirtMap).reduce((acc, [id, size]) => 
        {
            return acc + size.quantity;
        },0);

        const temp = props.itemConfiguration.sizeConfiguration[0] ? 
            formatCurrencyValue(totalPrice, props.itemConfiguration.sizeConfiguration[0].unitPrice.currency.formatting)
            :
            {
                formatted: "$0.00",
                string: "0.00",
                float: 0
            };
        
        props.onPriceDisplayChange(temp.formatted ?? "$0.00");

        const orderDelta = quantity - totalQty;

        const template: CurrencyAmount = props.itemConfiguration.sizeConfiguration[0] ? {
            ...props.itemConfiguration.sizeConfiguration[0].unitPrice,
            value: temp
        }
        :
        {
            currency: {
                name: 'USD',
                localization: Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }),
                formatting: {
                    prefix: '$',
                    suffix: '',
                    decimalSeparator: '.',
                    groupSeparator: ',',
                    decimalLimit: 2
                }
            },
            value: {
                formatted: "$0.00",
                string: "0.00",
                float: 0
            }
        }

        const data : ShirtData = {
            totalQuantity: quantity,
            sizes: Object.values(shirtMap)
        }

        if(orderDelta === 0)
        {
            props.onDataChange(props.lineItemId, data, true);
            props.onPriceChange(props.lineItemId, template);
            props.onPriceValidityChange(props.lineItemId, true);
            setErrorMsg(undefined);
        }
        else if(orderDelta > 0)
        {
            props.onPriceValidityChange(props.lineItemId, false);
            props.onDataChange(props.lineItemId, data, false, 'Waiting on shirt size selection.');
            setErrorMsg('Please add shirts to your order.')
        }
        else
        {
            props.onPriceValidityChange(props.lineItemId, false);
            props.onDataChange(props.lineItemId, data, false, 'Waiting on shirt size selection.');
            setErrorMsg('Please remove shirts from your order.')
        }

    },[shirtMap, quantity]);

    //TODO: Once we have item variation specific inventory, sum all item variations to get max permitted inventory
    //For now, the total inventory available is copied to all item variations, so...
    const max = Object.values(shirtMap)[0].maxInventory;

    return(<div>
        <QtySelector 
            quantity={quantity}
            onQuantityChange={onTotalQuantityChange}
            disableInputs={false}
            minValue={0}
            maxValue={max}
        />
        <p>Select Sizes for Shirts</p>
        {renderShirtSizes()}
        <p className="checkoutPageTextError">{errorMsg}</p>
    </div>)
}

interface ShirtSizeLineProps
{
    max: number,
    shirtDbId: number,
    shirtDisplayName: string
    shirtData: SizeOrder,
    disableInputs?: boolean,
    shirtUnitPrice: CurrencyAmount,
    onSizeDataChange: (dbId: number, quantity: number) => void
}

const ShirtSizeLine = (props: ShirtSizeLineProps) =>
{
    const onQuantityChange = (newQuant: number) =>
    {
        props.onSizeDataChange(props.shirtDbId, newQuant);
    }

    const sizeUnitPrice = props.shirtData.unitPrice;

    const prefixSymbol = (base: CurrencyAmount, variant: CurrencyAmount) =>
    {
        const difference = (variant.value.float ?? 0) - (base.value.float ?? 0);
        if (difference > 0)
        {
            return "+$";
        }
    }

    const modifierFormatting: CurrencyFormattingDetails = 
    {
        ...props.shirtUnitPrice.currency.formatting,
        prefix: prefixSymbol(props.shirtUnitPrice, sizeUnitPrice)
    }

    const priceModifier = formatCurrencyValue((sizeUnitPrice.value.float ?? 0) - (props.shirtUnitPrice.value.float ?? 0),modifierFormatting);

    console.log(props.max);

    return(<div>
        <div className='sizeNameDisplay'>
            <p>{props.shirtDisplayName}</p>
            {priceModifier.float != 0 && <p className="sizeDisplayModifier">{priceModifier.formatted}</p>}
        </div>
        <QtySelector 
            quantity={props.shirtData.quantity}
            onQuantityChange={onQuantityChange}
            disableInputs={false}
            minValue={0}
            maxValue={props.max}
        />
    </div>)
}

export default ShirtSizeSelector